import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { fetchAllLeads, Lead } from "../../../api/lead";
import { toast } from "react-toastify";
import ScrollToTop from "../../../core/common/ScrollToTop";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Filler,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { all_routes } from "../../router/all_routes";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import Tip from "../../../core/common/tooltip";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Filler
);
type Period =
  | "global"
  | "week"
  | "thisMonth"
  | "lastMonth"
  | "threeMonths"
  | "sixMonths"
  | "thisYear"
  | "lastYear"
  | "overall";

const InfoIcon = ({ text }: { text: string }) => (
  <Tip text={text} placement="top">
    <i
      className="bi bi-info-circle ms-2 text-secondary"
      style={{ cursor: "pointer", fontSize: 15 }}
    />
  </Tip>
);

const LeadInsights = () => {
  const route = all_routes;
  const userandling = useSelector((state: RootState) => state.auth.user_id);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const [globalPeriod, setGlobalPeriod] = useState<Period>("thisMonth");
  const [overallPeriod, setOverallPeriod] = useState<Period>("global");
  const [ownerPeriod, setOwnerPeriod] = useState<Period>("global");
  const [productPeriod, setProductPeriod] = useState<Period>("global");
  const [sourcePeriod, setSourcePeriod] = useState<Period>("global");
  const [stagePeriod, setStagePeriod] = useState<Period>("global");
  const [industryPeriod, setIndustryPeriod] = useState<Period>("global");
  const [typePeriod, setTypePeriod] = useState<Period>("global");
  const [productTypePeriod, setProductTypePeriod] = useState<Period>("global");
  const [customerPeriod, setCustomerPeriod] = useState<Period>("global");

  const periodOptions = [
    { value: "global" as const, label: "Use Global Filter" },
    { value: "week" as const, label: "Last 1 Week" },
    { value: "thisMonth" as const, label: "This Month" },
    { value: "lastMonth" as const, label: "Last Month" },
    { value: "threeMonths" as const, label: "Past 3 Months" },
    { value: "sixMonths" as const, label: "Past 6 Months" },

    // ⭐ NEW YEAR FILTERS
    { value: "thisYear" as const, label: "This Year" },
    { value: "lastYear" as const, label: "Last Year" },

    { value: "overall" as const, label: "Overall" },
  ];

  // Helper: Get date range
  const getDateRange = useCallback(
    (
      period:
        | "week"
        | "thisMonth"
        | "lastMonth"
        | "threeMonths"
        | "sixMonths"
        | "thisYear"
        | "lastYear"
        | "overall"
    ) => {
      const now = new Date();
      let start: Date;
      let end: Date = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59
      );

      switch (period) {
        case "week":
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;

        case "thisMonth":
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          break;

        case "lastMonth":
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          break;

        case "threeMonths":
          start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;

        case "sixMonths":
          start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          break;

        // ⭐ NEW: Start of current year
        case "thisYear":
          start = new Date(now.getFullYear(), 0, 1);
          break;

        // ⭐ NEW: Start of last year
        case "lastYear":
          start = new Date(now.getFullYear() - 1, 0, 1);
          end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
          break;

        case "overall":
        default:
          start = new Date(0);
          break;
      }

      return { start, end };
    },
    []
  );

  // Filter leads by effective period
  const getFilteredLeads = useCallback(
    (localPeriod: Period) => {
      let effectivePeriod =
        localPeriod === "global" ? globalPeriod : localPeriod;

      // 🔥 FIX: prevent invalid value for getDateRange()
      if (effectivePeriod === "global") {
        effectivePeriod = "overall";
      }

      if (effectivePeriod === "overall") {
        return leads;
      }

      const { start, end } = getDateRange(effectivePeriod);

      return leads.filter((lead) => {
        const date = new Date(lead.created_at);
        return date >= start && date <= end;
      });
    },
    [leads, globalPeriod, getDateRange]
  );

  // Load leads
  useEffect(() => {
    const loadLeads = async () => {
      try {
        setLoading(true);
        const data = await fetchAllLeads();
        setLeads(data || []);
      } catch (err) {
        console.error("Error loading leads", err);
        toast.error("Failed to load leads.");
      } finally {
        setLoading(false);
      }
    };
    loadLeads();
  }, []);
  
  // 1. Overall Status Activity
  const overallFilteredLeads = useMemo(
    () => getFilteredLeads(overallPeriod),
    [getFilteredLeads, overallPeriod]
  );
  const overallStatusCounts = useMemo(() => {
    const counts = { Not_Contacted: 0, Contacted: 0, Closed: 0, Lost: 0 };
    overallFilteredLeads.forEach((lead) => {
      counts[lead.lead_status as keyof typeof counts]++;
    });
    return counts;
  }, [overallFilteredLeads]);

  const overallDoughnutData = {
    labels: ["Not Contacted", "Contacted", "Closed", "Lost"],
    datasets: [
      {
        data: Object.values(overallStatusCounts),
        backgroundColor: ["#FFC107", "#4CAF50", "#2196F3", "#F44336"],
        borderColor: "#fff",
        borderWidth: 3,
      },
    ],
  };
  const overallDoughnutOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,

          generateLabels(chart) {
            const dataset = chart.data.datasets[0];

            const dataArray = (dataset.data as unknown as number[]) || [];
            const bgColors =
              (dataset.backgroundColor as unknown as string[]) || [];

            return (chart.data.labels || []).map(
              (label: any, index: number | undefined) => {
                // Guard: if index is invalid
                if (index === undefined || index === null || index < 0) {
                  return {
                    text: label,
                    fillStyle: "#ccc",
                    hidden: true,
                    datasetIndex: 0,
                    index: -1,
                  };
                }

                const value = dataArray[index] ?? 0;

                const meta = chart.getDatasetMeta(0);
                const item = meta.data[index] as any;
                const hidden = item?.hidden ?? false;

                return {
                  text: `${label} (${value})`,
                  fillStyle: bgColors[index],
                  strokeStyle: bgColors[index],
                  lineWidth: hidden ? 2 : 0,
                  hidden,
                  datasetIndex: 0,
                  index,
                };
              }
            );
          },
        },

        onClick: (evt, legendItem, legend) => {
          const chart = (legend as any).chart;
          const index = legendItem.index;

          if (index === undefined || index === null || index < 0) return;

          const meta = chart.getDatasetMeta(0);
          const item = meta.data[index] as any;

          item.hidden = !item.hidden;
          chart.update();
        },
      },

      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const dataset = ctx.dataset;
            const dataArray = (dataset.data as unknown as number[]) || [];
            const total = dataArray.reduce(
              (sum: number, val: number) => sum + (val || 0),
              0
            );

            const value = ctx.parsed || 0;
            const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";

            return `${ctx.label}: ${value} (${percent}%)`;
          },
        },
      },
    },
  };

  // 2. Lead Owner Activity
  const ownerFilteredLeads = useMemo(
    () => getFilteredLeads(ownerPeriod),
    [getFilteredLeads, ownerPeriod]
  );
  const ownerData = useMemo(() => {
    const map: Record<string, any> = {};
    ownerFilteredLeads.forEach((lead) => {
      const name = lead.users?.name || "Unassigned";
      if (!map[name])
        map[name] = {
          total: 0,
          Not_Contacted: 0,
          Contacted: 0,
          Closed: 0,
          Lost: 0,
        };
      map[name].total++;
      map[name][lead.lead_status]++;
    });
    return Object.entries(map)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [ownerFilteredLeads]);

  const ownerBarData = {
    labels: ownerData.map((o) => o.name),
    datasets: [
      {
        label: "Not Contacted",
        data: ownerData.map((o) => o.Not_Contacted),
        backgroundColor: "#FFC107",
      },
      {
        label: "Contacted",
        data: ownerData.map((o) => o.Contacted),
        backgroundColor: "#4CAF50",
      },
      {
        label: "Closed",
        data: ownerData.map((o) => o.Closed),
        backgroundColor: "#2196F3",
      },
      {
        label: "Lost",
        data: ownerData.map((o) => o.Lost),
        backgroundColor: "#F44336",
      },
    ],
  };
  const ownerBarOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
          padding: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const datasetLabel = ctx.dataset.label || "";
            const value = ctx.parsed.y || 0;

            const totalForOwner = ctx.chart.data.datasets
              .map((d: any) => d.data[ctx.dataIndex])
              .reduce((a: number, b: number) => a + b, 0);

            const percent = totalForOwner
              ? ((value / totalForOwner) * 100).toFixed(1)
              : "0.0";

            return `${datasetLabel}: ${value} (${percent}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          callback: function (value, index) {
            const label = this.getLabelForValue(index);
            const total = ownerData[index]?.total || 0;
            return `${label} (${total})`; // 👈 Add totals next to owners
          },
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        stacked: true,
        ticks: { precision: 0 },
      },
    },
  };

  // 3. Product Wise Leads
  const productFilteredLeads = useMemo(
    () => getFilteredLeads(productPeriod),
    [getFilteredLeads, productPeriod]
  );
  const productData = useMemo(() => {
    const map: Record<string, any> = {};
    productFilteredLeads.forEach((lead) => {
      const name = lead.products?.product_name || "Unknown Product";
      if (!map[name])
        map[name] = {
          total: 0,
          Not_Contacted: 0,
          Contacted: 0,
          Closed: 0,
          Lost: 0,
          value: 0,
        };
      map[name].total++;
      map[name][lead.lead_status]++;
      map[name].value += parseFloat(lead.lead_value || "0");
    });
    return Object.entries(map)
      .map(([name, data]) => ({
        name,
        ...data,
        avgValue: data.total > 0 ? data.value / data.total : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [productFilteredLeads]);

  const createGradient = (
    ctx: CanvasRenderingContext2D,
    start: string,
    end: string
  ) => {
    const gradient = ctx.createLinearGradient(0, 0, 300, 0);
    gradient.addColorStop(0, start);
    gradient.addColorStop(1, end);
    return gradient;
  };

  const productBarData = {
    labels: productData.map((p) => p.name),
    datasets: [
      {
        label: "Not Contacted",
        data: productData.map((p) => p.Not_Contacted),
        backgroundColor: "#FFC107",
      },
      {
        label: "Contacted",
        data: productData.map((p) => p.Contacted),
        backgroundColor: "#4CAF50",
      },
      {
        label: "Closed",
        data: productData.map((p) => p.Closed),
        backgroundColor: "#2196F3",
      },
      {
        label: "Lost",
        data: productData.map((p) => p.Lost),
        backgroundColor: "#F44336",
      },
    ],
  };
  const productBarOptions: ChartOptions<"bar"> = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,

    animation: false,

    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
          padding: 14,
        },
      },
    },

    scales: {
      x: { stacked: true, ticks: { precision: 0 } },
      y: {
        stacked: true,
        ticks: {
          callback: (value, index) => {
            const total = productData[index]?.total || 0;
            const name = productData[index]?.name || "";
            return `${name} (${total})`;
          },
        },
      },
    },
  };
  const ProductGradientPlugin = {
  id: "productGradient",
  beforeDatasetsDraw(chart : any) {
    const ctx = chart.ctx;

    chart.data.datasets.forEach((dataset : any, i : any) => {
      const baseColors = [
        ["#FFC107", "#FFD54F"],
        ["#4CAF50", "#81C784"],
        ["#2196F3", "#64B5F6"],
        ["#F44336", "#E57373"],
      ];

      const [start, end] = baseColors[i];

      const gradient = ctx.createLinearGradient(0, 0, 300, 0);
      gradient.addColorStop(0, start);
      gradient.addColorStop(1, end);

      dataset.backgroundColor = gradient;
    });
  },
};
  const BarCenterLabelsPlugin = {
    id: "centerLabels",
    afterDatasetsDraw(chart: any) {
      const { ctx, data } = chart;
      ctx.save();
      ctx.font = "bold 12px sans-serif";
      ctx.fillStyle = "#333";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      chart.getDatasetMeta(0).data.forEach((bar: any, index: number) => {
        const total = data.datasets
          .map((d: any) => d.data[index])
          .reduce((a: number, b: number) => a + b, 0);

        const x = bar.x;
        const y = bar.y;

        // Only show when bar has visible height
        if (total > 0) {
          ctx.fillText(total, x + 20, y); // Adjust X slightly for horizontal bars
        }
      });

      ctx.restore();
    },
  };

  // 4. Lead Source Effectiveness
  const sourceFilteredLeads = useMemo(
    () => getFilteredLeads(sourcePeriod),
    [getFilteredLeads, sourcePeriod]
  );
  const sourceData = useMemo(() => {
    const map: Record<string, any> = {};
    sourceFilteredLeads.forEach((lead) => {
      const name = lead.leadsource?.LeadSource_name || "Unknown Source";
      if (!map[name]) map[name] = { total: 0, Closed: 0 };
      map[name].total++;
      if (lead.lead_status === "Closed") map[name].Closed++;
    });
    return Object.entries(map)
      .map(([name, data]) => ({
        name,
        total: data.total,
        conversionRate: data.total > 0 ? (data.Closed / data.total) * 100 : 0,
      }))
      .sort((a, b) => b.conversionRate - a.conversionRate);
  }, [sourceFilteredLeads]);

  const sourcePieData = {
    labels: sourceData.map((s) => `${s.name} (${s.total})`),
    datasets: [
      {
        data: sourceData.map((s) => s.total),
        backgroundColor: sourceData.map(
          (_, i) => `hsl(${(i * 360) / sourceData.length}, 70%, 50%)`
        ),
      },
    ],
  };
  const createGradientLeadsource = (
    ctx: CanvasRenderingContext2D,
    index: number
  ) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, `hsl(${index * 40}, 80%, 60%)`);
    gradient.addColorStop(1, `hsl(${index * 40}, 80%, 50%)`);
    return gradient;
  };
  const LeadSourceGradientPlugin = {
  id: "leadSourceGradient",
  beforeDatasetsDraw(chart : any) {
    const ctx = chart.ctx;
    chart.data.datasets[0].backgroundColor = chart.data.labels.map((_ : any, i : number) => {
      const g = ctx.createLinearGradient(0, 0, 0, 200);
      g.addColorStop(0, `hsl(${i * 40}, 80%, 60%)`);
      g.addColorStop(1, `hsl(${i * 40}, 80%, 50%)`);
      return g;
    });
  },
};


  const sourcePieOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,

    animation: false,

    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 14,

          generateLabels(chart) {
            const dataset = chart.data.datasets[0];
            const data = (dataset.data as number[]) || [];

            const total = data.reduce((a, b) => a + b, 0);

            return (chart.data.labels || []).map(
              (label: any, index: number | undefined) => {
                if (index === undefined || index === null) {
                  return {
                    text: label,
                    fillStyle: "#ccc",
                    hidden: true,
                    index: -1,
                  };
                }

                const value = data[index] ?? 0;

                const meta = chart.getDatasetMeta(0);
                const element: any = meta.data[index];
                const hidden = element?.hidden ?? false;

                const percent = total
                  ? ((value / total) * 100).toFixed(1)
                  : "0.0";

                return {
                  text: `${label} (${value} • ${percent}%)`,
                  fillStyle: "#ddd",
                  strokeStyle: "#ddd",
                  lineWidth: hidden ? 2 : 0,
                  hidden,
                  index,
                  datasetIndex: 0,
                };
              }
            );
          },
        },

        onClick(evt, legendItem, legend) {
          const chart = (legend as any).chart;
          const index = legendItem.index;

          if (index === undefined || index < 0) return;

          const meta = chart.getDatasetMeta(0);
          const item: any = meta.data[index];

          item.hidden = !item.hidden;
          chart.update();
        },
      },

      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const dataset = ctx.dataset;
            const data = dataset.data as number[];
            const total = data.reduce((a, b) => a + b, 0);

            const value = ctx.parsed;
            const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";

            return `${ctx.label}: ${value} (${percent}%)`;
          },
        },
      },
    },
  };

  // 5. Lead Stage Distribution (Time Series)
  const stageFilteredLeads = useMemo(
    () => getFilteredLeads(stagePeriod),
    [getFilteredLeads, stagePeriod]
  );
  const stageTimeSeries = useMemo(() => {
    const monthly: Record<string, number> = {};
    stageFilteredLeads.forEach((lead) => {
      const date = new Date(lead.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      monthly[key] = (monthly[key] || 0) + 1;
    });
    const sorted = Object.entries(monthly).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    return {
      labels: sorted.map(([k]) => k),
      data: sorted.map(([, v]) => v),
    };
  }, [stageFilteredLeads]);

  const stageLineData = {
    labels: stageTimeSeries.labels,
    datasets: [
      {
        label: "Leads Created",
        data: stageTimeSeries.data,
        borderColor: "#2196F3",
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };
  const stageLineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed.y || 0;

            // show delta compared to previous month
            const index = ctx.dataIndex;
            const prev =
              index > 0 ? ctx.chart.data.datasets[0].data[index - 1] : null;

            const trend =
              prev != null && prev > 0
                ? (((value - prev) / prev) * 100).toFixed(1)
                : "0.0";

            return `Leads: ${value} (${trend}% change)`;
          },
        },
      },
    },

    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: "#e5e5e5" },
      },
      x: { grid: { display: false } },
    },
  };

  // 6. Industry Distribution
  const industryFilteredLeads = useMemo(
    () => getFilteredLeads(industryPeriod),
    [getFilteredLeads, industryPeriod]
  );
  const industryData = useMemo(() => {
    const map: Record<string, number> = {};
    industryFilteredLeads.forEach((lead) => {
      const name = lead.leadindustry?.LeadIndustry_name || "Unknown Industry";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [industryFilteredLeads]);

  const industryDoughnutData = {
    labels: industryData.map((i) => i.name),
    datasets: [
      {
        data: industryData.map((i) => i.total),
        backgroundColor: industryData.map((_, i) => `hsl(${i * 60}, 70%, 50%)`),
      },
    ],
  };
  const IndustryGradientPlugin = {
  id: "industryGradient",
  beforeDatasetsDraw(chart : any) {
    const ctx = chart.ctx;
    chart.data.datasets[0].backgroundColor = chart.data.labels.map((_ : any, i : any) => {
      const g = ctx.createLinearGradient(0, 0, 0, 200);
      g.addColorStop(0, `hsl(${i * 60}, 70%, 55%)`);
      g.addColorStop(1, `hsl(${i * 60}, 70%, 45%)`);
      return g;
    });
  },
};

  const industryPieOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,

          generateLabels(chart) {
            const dataset = chart.data.datasets[0];
            const data = dataset.data as number[];
            const total = data.reduce((a, b) => a + b, 0);

            return (chart.data.labels || []).map(
              (label: any, index: number) => {
                const value = data[index] ?? 0;

                const meta = chart.getDatasetMeta(0);
                const item: any = meta.data[index];
                const hidden = item?.hidden ?? false;

                const percent = total ? ((value / total) * 100).toFixed(1) : 0;

                return {
                  text: `${label} (${value} • ${percent}%)`,
                  fillStyle: "#ddd",
                  strokeStyle: "#ddd",
                  lineWidth: hidden ? 2 : 0,
                  hidden,
                  index,
                  datasetIndex: 0,
                };
              }
            );
          },
        },
        onClick(evt, legendItem, legend) {
          const chart = (legend as any).chart;
          const index = legendItem.index;
          if (index === undefined || index < 0) return;

          const meta = chart.getDatasetMeta(0);
          const item: any = meta.data[index];

          item.hidden = !item.hidden;
          chart.update();
        },
      },

      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed;
            const data = ctx.dataset.data as number[];
            const total = data.reduce((a, b) => a + b, 0);

            const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";

            return `${ctx.label}: ${value} (${percent}%)`;
          },
        },
      },
    },
  };

  // 7. Lead Type Distribution
  const typeFilteredLeads = useMemo(
    () => getFilteredLeads(typePeriod),
    [getFilteredLeads, typePeriod]
  );
  const typeData = useMemo(() => {
    const map: Record<string, number> = {};
    typeFilteredLeads.forEach((lead) => {
      const name = lead.leadtype?.leadtype_name || "Unknown Type";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [typeFilteredLeads]);

  // 8. Product Type Distribution
  const productTypeFilteredLeads = useMemo(
    () => getFilteredLeads(productTypePeriod),
    [getFilteredLeads, productTypePeriod]
  );
  const productTypeData = useMemo(() => {
    const map: Record<string, number> = {};
    productTypeFilteredLeads.forEach((lead) => {
      const name = lead.producttype?.product_name || "Unknown Product Type";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [productTypeFilteredLeads]);

  // 9. Top Customers
  const customerFilteredLeads = useMemo(
    () => getFilteredLeads(customerPeriod),
    [getFilteredLeads, customerPeriod]
  );
  const customerData = useMemo(() => {
    const map: Record<string, { total: number; value: number }> = {};
    customerFilteredLeads.forEach((lead) => {
      const name =
        lead.customertypes?.customerPotential_name || "Unknown Customer";
      if (!map[name]) map[name] = { total: 0, value: 0 };
      map[name].total++;
      map[name].value += parseFloat(lead.lead_value || "0");
    });
    return Object.entries(map)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.value - a.value);
  }, [customerFilteredLeads]);

  const customerBarData = {
    labels: customerData.map((c) => c.name),
    datasets: [
      {
        label: "Value",
        data: customerData.map((c) => c.value), // ₹
        backgroundColor: "rgba(33,150,243,0.7)",
      },
    ],
  };

  // add count array for plugin
  const customerLeadCounts = customerData.map((c) => c.total);

  const customerBarOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",

    plugins: {
      legend: { display: false },

      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed.x;
            const count = customerLeadCounts[ctx.dataIndex];

            const totalValue = ctx.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percent = totalValue
              ? ((value / totalValue) * 100).toFixed(1)
              : "0.0";

            return [
              `Value: ₹${value.toLocaleString()}`,
              `Leads: ${count}`,
              `Share: ${percent}%`,
            ];
          },
        },
      },
    },

    scales: {
      x: {
        ticks: {
          callback: (value) => "₹" + Number(value).toLocaleString(),
        },
      },
    },
  };

  const CustomerValueCountLabelPlugin = {
    id: "customerValueCountLabels",
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;

      ctx.save();
      ctx.font = "600 12px sans-serif";
      ctx.textBaseline = "middle";

      const dataset = chart.getDatasetMeta(0);

      dataset.data.forEach((bar: any, index: number) => {
        const value = chart.data.datasets[0].data[index];
        const count = customerLeadCounts[index];

        if (!value) return;

        const barX = bar.x;
        const barY = bar.y;

        // Value inside bar
        ctx.fillStyle = "#fff";
        ctx.fillText(`₹${value.toLocaleString()}`, barX - 70, barY);

        // Count outside bar
        ctx.fillStyle = "#333";
        ctx.fillText(`${count} leads`, barX + 20, barY);
      });

      ctx.restore();
    },
  };

  // Reusable Components
  const renderStatusCard = (
    title: string,
    count: number,
    color: string,
    icon: string
  ) => (
    <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 d-flex">
   
    <div className={`card dashboard-card bg-${color}-subtle border-0 w-100`}>
 
      <div className="card-body d-flex align-items-center">
     
        <div className={`avatar avatar-lg bg-${color} text-white`}>
          <i className={`ti ${icon} fs-24`} />
        </div>

        <div className="ms-3">
          <p className="mb-1 text-muted">{title}</p>
          <h4 className="mb-0 fw-bold">{count.toLocaleString()}</h4>
        </div>
      </div>
    </div>
  </div>

  );

  const renderFilterDropdown = (
    value: any,
    onChange: (val: any) => void,
    placeholder: string = "Select Period"
  ) => (
    <Dropdown className="dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="btn-sm">
        {periodOptions.find((p) => p.value === value)?.label || placeholder}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {periodOptions.map((opt) => (
          <Dropdown.Item key={opt.value} onClick={() => onChange(opt.value)}>
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  // ------------------------------------------
  // Leads By Year (New Block)
  const leadsByYear = useMemo(() => {
    const year =
      globalPeriod === "thisYear"
        ? new Date().getFullYear()
        : globalPeriod === "lastYear"
        ? new Date().getFullYear() - 1
        : selectedYear;

    const prevYear = year - 1;

    const monthly = Array.from({ length: 12 }, () => ({
      current: 0,
      previous: 0,
    }));

    leads.forEach((lead) => {
      const d = new Date(lead.created_at);
      const m = d.getMonth();
      const y = d.getFullYear();

      if (y === year) monthly[m].current++;
      if (y === prevYear) monthly[m].previous++;
    });

    const result = monthly.map((m, i) => {
      const mom =
        i === 0 || monthly[i - 1].current === 0
          ? 0
          : ((m.current - monthly[i - 1].current) / monthly[i - 1].current) *
            100;

      const yoy =
        m.previous === 0 ? 0 : ((m.current - m.previous) / m.previous) * 100;

      return {
        month: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ][i],
        current: m.current,
        previous: m.previous,
        mom,
        yoy,
      };
    });

    return { year, prevYear, data: result };
  }, [leads, globalPeriod, selectedYear]);

  const leadsByYearChart = {
    labels: leadsByYear.data.map((d) => d.month),
    datasets: [
      {
        label: leadsByYear.year.toString(),
        data: leadsByYear.data.map((d) => d.current),
        borderColor: "#2a8ef7",
        backgroundColor: "rgba(42,142,247,0.15)",
        borderWidth: 3,
        tension: 0.35,
        fill: true,
      },
      {
        label: leadsByYear.prevYear.toString(),
        data: leadsByYear.data.map((d) => d.previous),
        borderColor: "#b4b4b4",
        borderWidth: 2,
        borderDash: [6, 6],
        tension: 0.35,
      },
    ],
  };
  const leadsByYearOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 16,
        },
      },

      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<"line">) => {
            const value = ctx.parsed.y ?? 0;
            const i = ctx.dataIndex;
            return [
              `Leads: ${value}`,
              `MoM: ${leadsByYear.data[i].mom.toFixed(1)}%`,
              `YoY: ${leadsByYear.data[i].yoy.toFixed(1)}%`,
            ];
          },
        },
      },
    },

    scales: {
      y: { ticks: { precision: 0 } },
    },
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-body">
          <div className="text-center py-5">
            <i className="ti ti-loader-3 ti-spin fs-48 text-primary"></i>
            <p className="mt-3">Loading Lead Insights...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          {/* ======= COMBINED HEADER + GLOBAL FILTER ======= */}
          <div className="card shadow-sm border-0 rounded-4 p-3 mb-4 bg-light-subtle">
            <div className="row align-items-center">
              {/* Left Section — Title + Subtitle */}
              <div className="col-md-6 d-flex align-items-start gap-3">
                <div className="text-primary fs-3 mt-1">
                  <i className="bi bi-bar-chart-fill"></i>
                </div>

                <div>
                  <h3 className="mb-1 fw-semibold d-flex align-items-center gap-2">
                    Lead Insights Dashboard
                    <InfoIcon text="Complete analytics of all leads based on global filter" />
                  </h3>

                  <p className="text-muted small mb-1">
                    Analytics overview with global filtering
                  </p>

                  {/* Breadcrumb */}
                  <ol className="breadcrumb mb-0 small">
                    <li className="breadcrumb-item">
                      <Link to={route.leads} 
                       style={{
                        color: "#9595b5",
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#E41F07")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9595b5")
                      }
                      >
                        Leads
                      </Link>
                    </li>
                    <li className="breadcrumb-item active">Lead Insights</li>
                  </ol>
                </div>
              </div>

              {/* Right Section — Global Filter */}
              <div className="col-md-6 text-md-end mt-3 mt-md-0">
                <div className="d-inline-flex align-items-center gap-2 px-3 py-2 bg-white shadow-sm border">
                  <i className="bi bi-globe2 text-primary me-2"></i>
                  <div className="text-start me-2">
                    <div className="fw-semibold small">Global Time Filter</div>
                    <div className="text-muted small">
                      Applies to all sections
                    </div>
                  </div>

                  {renderFilterDropdown(
                    globalPeriod,
                    setGlobalPeriod,
                    "Choose Global Period"
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* Overall Status Cards */}
            {renderStatusCard(
              "Not Contacted",
              overallStatusCounts.Not_Contacted,
              "warning",
              "ti-user-x"
            )}
            {renderStatusCard(
              "Contacted",
              overallStatusCounts.Contacted,
              "success",
              "ti-phone"
            )}
            {renderStatusCard(
              "Closed",
              overallStatusCounts.Closed,
              "info",
              "ti-check"
            )}
            {renderStatusCard(
              "Lost",
              overallStatusCounts.Lost,
              "danger",
              "ti-trash"
            )}
          </div>

          <div className="row">
            {/* Overall Status Doughnut */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-pie-chart-fill text-primary"></i>
                    Overall Lead Status
                    <InfoIcon text="Lead status distribution for the selected period" />
                  </h5>

                  {renderFilterDropdown(overallPeriod, setOverallPeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || Object.values(overallStatusCounts).every(v => v === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Doughnut
                      data={overallDoughnutData}
                      options={overallDoughnutOptions}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Lead Owner Activity */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-people-fill text-primary"></i>
                    Lead Owner Activity
                    <InfoIcon text="Lead performance breakdown by assigned owner" />
                  </h5>

                  {renderFilterDropdown(ownerPeriod, setOwnerPeriod)}
                </div>

                <div className="card-body" style={{ height: 360 }}>
                  {leads.length === 0 || ownerData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Bar data={ownerBarData} options={ownerBarOptions} />
                  )}
                </div>
              </div>
            </div>

            {/* Product Wise */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-box-seam text-primary"></i>
                    Product Wise Leads
                    <InfoIcon text="Lead distribution and status grouped by product" />
                  </h5>

                  {renderFilterDropdown(productPeriod, setProductPeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || productData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Bar
                      data={productBarData}
                      plugins={[BarCenterLabelsPlugin, ProductGradientPlugin]}
                      options={productBarOptions}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Lead Source */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-bullseye text-primary"></i>
                    Lead Source Effectiveness
                    <InfoIcon text="Conversion performance across all lead sources" />
                  </h5>

                  {renderFilterDropdown(sourcePeriod, setSourcePeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || sourceData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Doughnut data={sourcePieData} options={sourcePieOptions}  plugins={[LeadSourceGradientPlugin]} />
                  )}
                </div>
              </div>
            </div>

            {/* Lead Stage Trend */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up-arrow text-primary"></i>
                    Lead Creation Trend
                    <InfoIcon text="Monthly trend of lead creation and growth change" />
                  </h5>

                  {renderFilterDropdown(stagePeriod, setStagePeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || stageLineData.datasets[0].data.every(v => v === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Line data={stageLineData} options={stageLineOptions} />
                  )}
                </div>
              </div>
            </div>

            {/* Industry */}
            <div className="col-xl-6 col-lg-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-building text-primary"></i>
                    Lead Industry Distribution
                    <InfoIcon text="Lead counts grouped by industry category" />
                  </h5>

                  {renderFilterDropdown(industryPeriod, setIndustryPeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || industryData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Doughnut
                      data={industryDoughnutData}
                      options={industryPieOptions}
                      plugins={[IndustryGradientPlugin]}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Top Customers */}
            <div className="row">
              <div className="col-lg-6 col-md-12">
                {/* ---------------- TOP CUSTOMERS ---------------- */}
                <div className="card shadow-sm border-0 rounded-4 mb-3">
                  <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
  <h5 className="mb-0 d-flex align-items-center gap-2">
    <i className="bi bi-people-fill text-primary"></i>
    Customers Type Leads by Value & Count
    <InfoIcon text="Lead value and count comparison across customer types" />
  </h5>

  <div
    style={{
      transform: "scale(0.9)",
      transformOrigin: "right center",
    }}
  >
    {renderFilterDropdown(customerPeriod, setCustomerPeriod)}
  </div>
</div>



                  <div className="card-body" style={{ height: 360 }}>
                    {leads.length === 0 || customerData.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                        <p className="text-muted mb-0 fw-semibold">
                          No data found for leads insights
                        </p>
                      </div>
                    ) : (
                      <Bar
                        data={customerBarData}
                        options={customerBarOptions}
                        plugins={[CustomerValueCountLabelPlugin]}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 col-md-12">
                <div className="card shadow-sm border-0 rounded-4 mb-3">
                  <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
  
  {/* LEFT */}
  <h5 className="mb-0 d-flex align-items-center gap-2">
    <i className="bi bi-stack text-primary"></i>
    Product Type Distribution
    <InfoIcon text="Leads grouped by product type with percentage share" />
  </h5>

  <div
    style={{
      transform: "scale(0.9)",
      transformOrigin: "right center",
    }}
  >
    {renderFilterDropdown(productTypePeriod, setProductTypePeriod)}
  </div>

</div>



                  <div className="card-body">
  {leads.length === 0 || productTypeData.length === 0 ? (
    <div className="text-center py-5">
      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
      <p className="text-muted mb-0 fw-semibold">
        No data found for leads insights
      </p>
    </div>
  ) : (
    productTypeData.map((pt, index) => {
      const totalLeads = productTypeData.reduce(
        (a, b) => a + b.total,
        0
      );
      const percent = totalLeads
        ? (pt.total / totalLeads) * 100
        : 0;

      const hue = (index * 360) / productTypeData.length + 120;
      const startColor = `hsl(${hue}, 70%, 55%)`;
      const endColor = `hsl(${hue}, 80%, 45%)`;

      return (
        <div key={pt.name} className="mb-3">
          {/* Label row */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1 gap-1">
            <strong className="text-truncate w-100 w-sm-auto">
              {pt.name}
            </strong>
            <span className="text-muted small text-nowrap">
              {pt.total} leads ({percent.toFixed(1)}%)
            </span>
          </div>

          {/* Progress bar */}
          <div
            className="progress rounded-3"
            style={{ height: 8, backgroundColor: "#f1f1f1" }}
          >
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${percent}%`,
                background: `linear-gradient(90deg, ${startColor}, ${endColor})`,
              }}
            />
          </div>
        </div>
      );
    })
  )}
</div>

                </div>
              </div>

              {/* ---------------- LEAD TYPE DISTRIBUTION ---------------- */}
              <div className="card shadow-sm border-0 rounded-4 mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="d-flex align-items-center gap-2">
                    <i className="bi bi-bar-chart-steps text-primary"></i>
                    Lead Type Distribution
                    <InfoIcon text="Lead counts grouped by type (e.g., new, existing)" />
                  </h5>

                  {renderFilterDropdown(typePeriod, setTypePeriod)}
                </div>

                <div className="card-body" style={{ height: 350 }}>
                  {leads.length === 0 || typeData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-badge text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for leads insights
                      </p>
                    </div>
                  ) : (
                    <Bar
                      data={{
                        labels: typeData.map((t) => t.name),
                        datasets: [
                          {
                            label: "Leads",
                            data: typeData.map((t) => t.total),
                            backgroundColor: typeData.map(
                              (_, i) =>
                                `hsl(${(i * 360) / typeData.length}, 70%, 60%)`
                            ),
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: "y",
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              const total = typeData.reduce(
                                (a, b) => a + b.total,
                                0
                              );
                              const value = ctx?.parsed?.x ?? 0;
                              const percent = total
                                ? ((value / total) * 100).toFixed(1)
                                : "0.0";
                              return `${value} leads (${percent}%)`;

                              return `${ctx.parsed.x} leads (${percent}%)`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: { ticks: { precision: 0 } },
                      },
                    }}
                  />
                  )}
                </div>
              </div>

              {/* ---------------- PRODUCT TYPE DISTRIBUTION ---------------- */}
              {/* <div className="card shadow-sm border-0 rounded-4 mb-3">
  <div className="card-header d-flex justify-content-between align-items-center">
    <h5 className="d-flex align-items-center gap-2">
      <i className="bi bi-bar-chart text-primary"></i>
      Product Type Distribution
    </h5>
    {renderFilterDropdown(productTypePeriod, setProductTypePeriod)}
  </div>

  <div className="card-body" style={{ height: 350 }}>
    <Bar
      data={{
        labels: productTypeData.map((pt) => pt.name),
        datasets: [
          {
            label: "Leads",
            data: productTypeData.map((pt) => pt.total),
            backgroundColor: productTypeData.map(
              (_, i) =>
                `hsl(${
                  (i * 360) / productTypeData.length + 120
                }, 70%, 60%)`
            ),
            borderWidth: 1,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = productTypeData.reduce((a, b) => a + b.total, 0);
             const value = ctx.parsed?.x ?? 0;
const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";
return `${value} leads (${percent}%)`;

              },
            },
          },
        },
        scales: {
          x: { ticks: { precision: 0 } },
        },
      }}
    />
  </div>
</div> */}
              {/* ===========================
     LEADS BY YEAR (NEW BLOCK)
=========================== */}
              <div className="card shadow-sm border-0 rounded-4 mb-4">
                {/* Header */}
                <div className="card-header bg-white border-bottom-0 pb-0">
                  <div className="d-flex justify-content-between align-items-center">
                    {/* Title + Icon */}
                    <div className="d-flex align-items-center gap-2">
                      <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3">
                        <i className="bi bi-bar-chart-steps me-2"></i>
                      </div>

                      <div>
                        <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                          <i className="bi bi-bar-chart-line-fill text-primary"></i>
                          Leads by Year
                          <InfoIcon text="Yearly lead trends with MoM and YoY comparison" />
                        </h5>

                        <small className="text-muted">
                          Monthly performance with MoM & YoY insights
                        </small>
                      </div>
                    </div>

                    {/* Year Dropdown */}
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <i className="bi bi-calendar4-week text-muted"></i>
                      <select
                        className="form-select form-select-sm"
                        value={selectedYear}
                        onChange={(e) =>
                          setSelectedYear(Number(e.target.value))
                        }
                        style={{ width: "120px" }}
                      >
                        <option value={new Date().getFullYear()}>
                          {new Date().getFullYear()}
                        </option>
                        <option value={new Date().getFullYear() - 1}>
                          {new Date().getFullYear() - 1}
                        </option>
                        <option value={new Date().getFullYear() - 2}>
                          {new Date().getFullYear() - 2}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* KPI Summary Row */}
                <div className="px-4 py-3 border-bottom bg-light-subtle">
                  <div className="row text-center g-3 ">
                    {/* Total Leads */}
                    <div className="col  dashboard-card ">
                      <div className="rounded-3 p-2 bg-white shadow-sm">
                        <h4 className="fw-bold mb-0">
                          {leadsByYear.data.reduce((a, b) => a + b.current, 0)}
                        </h4>
                        <small className="text-muted">
                          Total Leads ({leadsByYear.year})
                        </small>
                      </div>
                    </div>

                    {/* Highest Month */}
                    <div className="col  dashboard-card ">
                      <div className="rounded-3 p-2 bg-white shadow-sm">
                        <h6 className="text-primary fw-bold mb-0">
                          {Math.max(...leadsByYear.data.map((d) => d.current))}
                        </h6>
                        <small className="text-muted">Peak Month</small>
                      </div>
                    </div>

                    {/* Lowest Month */}
                    <div className="col  dashboard-card ">
                      <div className="rounded-3 p-2 bg-white shadow-sm">
                        <h6 className="text-danger fw-bold mb-0">
                          {Math.min(...leadsByYear.data.map((d) => d.current))}
                        </h6>
                        <small className="text-muted">Lowest Month</small>
                      </div>
                    </div>

                    {/* YoY Growth */}
                    <div className="col  dashboard-card ">
                      <div className="rounded-3 p-2 bg-white shadow-sm">
                        <h6
                          className={
                            ((leadsByYear.data.reduce(
                              (a, b) => a + b.current,
                              0
                            ) -
                              leadsByYear.data.reduce(
                                (a, b) => a + b.previous,
                                0
                              )) /
                              leadsByYear.data.reduce(
                                (a, b) => a + b.previous,
                                0
                              )) *
                              100 >=
                            0
                              ? "text-success fw-bold mb-0"
                              : "text-danger fw-bold mb-0"
                          }
                        >
                          {(
                            ((leadsByYear.data.reduce(
                              (a, b) => a + b.current,
                              0
                            ) -
                              leadsByYear.data.reduce(
                                (a, b) => a + b.previous,
                                0
                              )) /
                              leadsByYear.data.reduce(
                                (a, b) => a + b.previous,
                                0
                              )) *
                            100
                          ).toFixed(1)}
                          %
                        </h6>
                        <small className="text-muted">YoY Growth</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="card-body" style={{ height: 350 }}>
                  <Line data={leadsByYearChart} options={leadsByYearOptions} />
                </div>

                {/* Table Below */}
                <div className="table-responsive px-3 pb-3">
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Month</th>
                        <th>Leads</th>
                        <th>MoM</th>
                        <th>YoY</th>
                      </tr>
                    </thead>

                    <tbody>
                      {leadsByYear.data.map((m) => (
                        <tr key={m.month}>
                          <td className="fw-semibold">{m.month}</td>
                          <td>{m.current}</td>

                          <td
                            className={
                              m.mom >= 0 ? "text-success" : "text-danger"
                            }
                          >
                            {m.mom.toFixed(1)}%
                          </td>

                          <td
                            className={
                              m.yoy >= 0 ? "text-success" : "text-danger"
                            }
                          >
                            {m.yoy.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default LeadInsights;
